import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getStudent } from '@/game/GameComponents/PhaserGame/GameInstance';
import IngameLoadingScreen from '@/game/GameComponents/PhaserGame/Scenes/IngameLoadingScreen';
import { MainScene } from '@/game/GameComponents/PhaserGame/Scenes/MainScene';
import {
  SceneDirectory,
  SceneLaunchContext,
  SceneType,
} from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/SceneManagerTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { isPhaserDevMode } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';

/**
 *  Scene Manager Overview:
 *  _____________________________________
 *  Tracks which scenes have been loaded in the game.
 *  Because we want to be flexible, scenes can have subscenes e.g. TilemapScene is a subscene of the Levelmap.
 *  When you want to move to a new scene, you can just call launchScene and it will handle shutting down the
 *  old scene, subscenes etc.
 *  There are 3 scene types: gameplay, ui and abstract.
 *      - Gameplay scenes are the main scenes that the player sees and interacts with e.g. Levelmap, Minigame, Duel.
 *      - UI scenes are generally superimposed over gameplay scenes.
 *      - Abstract scenes aren't seen by the player and are just used for gameplay elements that persist in the background.
 */
export default class PhaserSceneManager {
  static Instance?: PhaserSceneManager;

  constructor(private mainScene: MainScene) {
    this.sceneDirectory = {};
    this.activeSceneKey = '';
    if (PhaserSceneManager.Instance !== undefined) {
      return;
    }
    PhaserSceneManager.Instance = this;

    this.initialize();
  }

  activeSceneKey: string;
  sceneDirectory: SceneDirectory;
  debug: boolean = false;
  debugStack: Phaser.GameObjects.DOMElement | undefined = undefined;

  getMainScene(): MainScene {
    return this.mainScene;
  }

  getScene(sceneKey: string): Phaser.Scene | undefined {
    const scene = this.sceneDirectory[sceneKey];
    if (scene) {
      return this.mainScene.scene.get(sceneKey) as Phaser.Scene;
    }
    // Else it could be a subscene.
    for (const scene of Object.keys(this.sceneDirectory)) {
      for (const subScene of this.sceneDirectory[scene]?.subScenes ?? []) {
        if (subScene.key === sceneKey) {
          return this.mainScene.scene.get(subScene.key) as Phaser.Scene;
        }
      }
    }
    return undefined;
  }

  /*
  Pauses a scene.
  @param sceneKey - The key of the scene to pause.
  @param hidePausedScene - Whether to hide the paused scene.
  */
  pauseScene(sceneKey: string, hidePausedScene: boolean = true) {
    if (
      !this.mainScene.scene.isActive(sceneKey) ||
      this.mainScene.scene.isPaused(sceneKey)
    )
      return;
    this.mainScene.scene.pause(sceneKey);
    if (hidePausedScene) {
      this.mainScene.scene.setVisible(false, sceneKey);
    }
  }

  /*
  Resumes a scene and makes it visible.
  @param sceneKey - The key of the scene to resume.
  */
  resumeScene(sceneKey: string) {
    if (
      this.mainScene.scene.isActive(sceneKey) &&
      !this.mainScene.scene.isPaused(sceneKey)
    ) {
      return;
    }
    this.mainScene.scene.resume(sceneKey);
    this.mainScene.scene.setVisible(true, sceneKey);
  }

  /*
  Restarts a scene and all its subscenes.
  @param sceneKey - The key of the scene to restart.
  @param clearSubScenes - If true, will clear the subscenes and not restart them.
  Useful for when you want to restart a scene and let it regenerate its own subscenes.
  */
  restartScene(sceneKey: string, clearSubScenes: boolean = false) {
    const sceneToRestart = this.mainScene.scene.get(sceneKey);
    if (!sceneToRestart) {
      console.warn('tried to restart scene', sceneKey, 'that did not exist.');
      return;
    }
    const sceneInDirectory = this.sceneDirectory[sceneKey];
    if (!sceneInDirectory) {
      console.warn(
        'tried to restart scene',
        sceneKey,
        'that was not in scene directory.',
      );
      return;
    }

    this.mainScene.scene.stop(sceneKey);

    // Stop the sub scenes.
    for (const subScene of this.sceneDirectory[sceneKey]?.subScenes ?? []) {
      this.mainScene.scene.stop(subScene.key);
    }

    if (clearSubScenes) {
      this.sceneDirectory[sceneKey].subScenes = [];
    }

    this.activeSceneKey = '';
    this.launchScene(sceneKey, sceneInDirectory.scene, {
      subScenes: [],
      type: 'gameplay',
    });
  }

  /*
  Launches or resumes a scene based in its current state. 
  Don't call this directly, use launchScene instead.
  @param sceneKey - The key of the scene to launch or resume.
  @param data - The data to pass to the scene.
  */
  private launchOrResumeScene(sceneKey: string, data?: any) {
    if (this.mainScene.scene.isPaused(sceneKey)) {
      this.mainScene.scene.resume(sceneKey);
    } else {
      this.mainScene.scene.launch(sceneKey, data);
    }
    this.mainScene.scene.bringToTop(sceneKey);
    this.mainScene.scene.setVisible(true, sceneKey);
  }

  /*
  Main way to launch a scene. Call this from anywhere in the code when you
  want to start up a new scene, or resume an existing one.
  @param sceneKey - The key of the scene to launch.
  @param scene - The scene class to launch. Phaser needs this.
  @param launchContext - The launch context to pass to the scene.
  */
  launchScene(
    sceneKey: string,
    scene: typeof Phaser.Scene,
    launchContext: SceneLaunchContext,
  ) {
    if (sceneKey === this.activeSceneKey) {
      console.warn('scene already active', sceneKey);
      return this.mainScene.scene.get(sceneKey) as Phaser.Scene;
    }

    if (launchContext.stopActiveScene) {
      this.mainScene.scene.stop(this.activeSceneKey);
      // Stop all subscenes.
      for (const subScene of this.sceneDirectory[this.activeSceneKey]
        ?.subScenes ?? []) {
        this.mainScene.scene.stop(subScene.key);
      }
    }

    let { subScenes = [], type, data } = launchContext;

    // Add the scene to the scene directory.
    if (!this.sceneDirectory[sceneKey]) {
      this.sceneDirectory[sceneKey] = {
        scene,
        type: launchContext.type,
        subScenes: subScenes,
      };
    } else {
      // Get the existing sub scenes.
      subScenes = this.sceneDirectory[sceneKey].subScenes;
    }

    if (launchContext.type === 'gameplay') {
      // Pause the active scene & subscenes.
      const activeScene = this.sceneDirectory[this.activeSceneKey];
      if (activeScene) {
        this.pauseScene(this.activeSceneKey);
        for (const subScene of activeScene.subScenes) {
          if (launchContext.removeSubScenes) {
            this.mainScene.scene.remove(subScene.key);
          } else {
            this.pauseScene(subScene.key);
          }
        }
        if (launchContext.removeSubScenes) {
          activeScene.subScenes = [];
        }
      }
    }
    if (launchContext.removeActiveScene) {
      this.mainScene.scene.remove(this.activeSceneKey);
      delete this.sceneDirectory[this.activeSceneKey];
    }

    // Add the scene.
    if (!this.mainScene.scene.get(sceneKey)) {
      this.mainScene.scene.add(sceneKey, scene);
      this.mainScene.scene.bringToTop(sceneKey);

      for (const subScene of subScenes) {
        this.mainScene.scene.add(subScene.key, subScene.scene);
        this.mainScene.scene.bringToTop(subScene.key);
      }
    }

    // Launch the main scene and subscenes.
    this.launchOrResumeScene(sceneKey, data);
    for (const subScene of subScenes) {
      this.launchOrResumeScene(subScene.key);
      this.mainScene.scene.moveBelow(sceneKey, subScene.key);
    }
    this.mainScene.scene.bringToTop(sceneKey);

    // Set the active scene key.
    if (type === 'gameplay') {
      this.activeSceneKey = sceneKey;
      // Fire an event.
      EventBus.emit(GAME_EVENTS.ACTIVE_SCENE_CHANGED, {
        sceneKey,
        launchContext,
      });
    }
    return this.mainScene.scene.get(sceneKey) as Phaser.Scene;
  }

  /*
  Launches a sub scene and adds it to the scene directory.
  @param parentSceneKey - The key of the parent scene.
  @param subSceneKey - The key of the sub scene.
  @param subscene - The scene class to launch. Phaser needs this.
  @param type - The type of the sub scene.
  */
  launchSubScene(
    parentSceneKey: string,
    subSceneKey: string,
    subscene: typeof Phaser.Scene,
    type: SceneType,
  ) {
    // Case for the scene not having launched yet.
    if (!this.mainScene.scene.get(subSceneKey)) {
      this.mainScene.scene.add(subSceneKey, subscene);
    }
    this.launchOrResumeScene(subSceneKey, subscene);
    this.mainScene.scene.bringToTop(subSceneKey);
    const existingSubScene = this.sceneDirectory[
      parentSceneKey
    ]?.subScenes?.find((s) => s.key === subSceneKey);
    if (existingSubScene) {
      // Remove the existing sub scene.
      this.sceneDirectory[parentSceneKey]?.subScenes?.splice(
        this.sceneDirectory[parentSceneKey]?.subScenes?.indexOf(
          existingSubScene,
        ),
        1,
      );
    }
    // Push the new sub scene.
    this.sceneDirectory[parentSceneKey]?.subScenes?.push({
      key: subSceneKey,
      scene: subscene,
      type,
    });
  }

  /*
  Use this function when you want to show some loading feedback while a scene is launching.
  This launches an ingame loading screen overlay over the parent scene to provide immedidate 
  feedback to the user.
  Perfect for UI scenes.
  @param parentScene - The parent scene.
  @param sceneKey - The key of the scene to launch.
  @param sceneClass - The scene class to launch. Phaser needs this.
  @param sceneData - The data to pass to the scene.
  */
  launchOverlayScene(
    parentScene: Phaser.Scene,
    sceneKey: string,
    sceneClass: typeof Phaser.Scene,
    sceneData?: any,
  ) {
    const { scene } = parentScene;
    if (scene.isPaused(sceneKey)) {
      this.pauseScene(this.activeSceneKey, false);
      scene.resume(sceneKey, sceneData);
      scene.bringToTop(sceneKey);
    } else {
      let loadingScreen = this.mainScene.scene.get(
        'IngameLoadingScreen',
      ) as IngameLoadingScreen;

      const loadingLogic = () => {
        loadingScreen.load.once('complete', () => {
          this.pauseScene(this.activeSceneKey, false);
          scene.stop('IngameLoadingScreen');
          this.launchScene(sceneKey, sceneClass, {
            type: 'ui',
            data: sceneData,
          });
        });

        loadingScreen.load.start();
      };

      if (loadingScreen && scene.isPaused('IngameLoadingScreen')) {
        (loadingScreen as IngameLoadingScreen).doLoading(loadingLogic);
      } else {
        loadingScreen = this.launchScene(
          'IngameLoadingScreen',
          IngameLoadingScreen,
          {
            type: 'ui',
            data: { doLoad: loadingLogic },
          },
        ) as IngameLoadingScreen;
      }
    }
    scene.setVisible(true, sceneKey);
  }

  initialize() {
    if (this.debug && isPhaserDevMode(getStudent())) {
      this.mainScene.add.dom(0, 0).createFromHTML(`
        <style>
          #active-scene {
            color: green;
          }
          li {
            color: white;
          }
          ul {
            background-color: black;
            text-align: left;
          }
          .paused {
            color: grey;
          }
            
        </style>`);

      this.mainScene.events.on('update', () => {
        if (this.debugStack) {
          this.debugStack.destroy();
        }
        this.debugStack = this.mainScene.add.dom(200, 100).createFromHTML(`
          <ul>
            ${Object.keys(this.sceneDirectory)
              .sort((a, b) => {
                return (
                  this.mainScene.scene.getIndex(a) -
                  this.mainScene.scene.getIndex(b)
                );
              })
              .map((key) => {
                const id = key === this.activeSceneKey ? 'active-scene' : '';
                const paused =
                  !this.mainScene.scene.isActive(key) &&
                  !this.mainScene.scene.isVisible(key)
                    ? 'paused'
                    : '';
                return `<li id="${id}" class="${paused}">${key}-${this.getStatusName(
                  this.mainScene.scene.getStatus(key),
                )}</li><ul>${this.sceneDirectory[key].subScenes
                  .map((subScene) => {
                    const paused =
                      !this.mainScene.scene.isActive(subScene.key) &&
                      !this.mainScene.scene.isVisible(key)
                        ? 'paused'
                        : '';
                    return `<li class="${paused}">${
                      subScene.key
                    }-${this.getStatusName(
                      this.mainScene.scene.getStatus(subScene.key),
                    )}</li>`;
                  })
                  .join('')}</ul>`;
              })
              .join('')}
          </ul>
          `);
      });
    }
  }

  // Just used for debugging. Why doesn't Phaser make these easily accessible??
  getStatusName(statusNumber: number) {
    const SCENE_STATUS = {
      0: 'PENDING',
      1: 'INIT',
      2: 'START',
      3: 'LOADING',
      4: 'CREATING',
      5: 'RUNNING',
      6: 'PAUSED',
      7: 'SLEEPING',
      8: 'SHUTDOWN',
      9: 'DESTROYED',
    };

    return SCENE_STATUS[statusNumber as keyof typeof SCENE_STATUS] ?? undefined;
  }

  static destroySceneManager() {
    PhaserSceneManager.Instance = undefined;
  }
}

// Main way to get the scene manager instance.
export const getSceneManager = () => PhaserSceneManager.Instance;
